import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  FaUpload, 
  FaFileAlt
} from 'react-icons/fa';
import { usePageContent } from '../services/pageContentService';
import HtmlContent from '../components/HtmlContent';
import RawHtmlPage, { isRawHtmlPage } from '../components/RawHtmlPage';

const AuthorGuidelinesPage = () => {
  const location = useLocation();
  const { content: liveContent, loading } = usePageContent('author_guidelines');

  // Smooth scroll to anchor if hash is present in URL
  useEffect(() => {
    if (location.hash) {
      const hashId = location.hash.replace('#', '');
      const timer = setTimeout(() => {
        const el = document.getElementById(hashId);
        if (el) {
          const header = document.querySelector('header');
          const headerHeight = header ? header.offsetHeight : 140;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - (headerHeight + 20);
          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const titles = liveContent?.titles || {};

  const defaultArticleTypes = [
    { title: 'Original Research Articles', words: '3,000–5,000 words', desc: 'Controlled clinical trials, basic ultra-dilution pharmacology investigations, observational studies, or epidemiological surveys.' },
    { title: 'Clinical Case Reports', words: '1,500–3,000 words', desc: 'Adheres to CARE guidelines. Detailed homoeopathic symptom totality, rubrics, repertorisation chart, and long-term follow-up.' },
    { title: 'Review Articles & Meta-Analyses', words: '4,000–6,000 words', desc: 'Comprehensive critical evaluation of existing homoeopathic and integrative biomedical literature (PRISMA compliant).' },
    { title: 'Short Communications & Letters', words: '1,000–2,000 words', desc: 'Brief reports of novel pilot investigations, urgent preliminary findings, or scholarly commentary on recently published papers.' }
  ];

  const articleTypes = (Array.isArray(liveContent?.article_types) && liveContent.article_types.length > 0)
    ? liveContent.article_types
    : defaultArticleTypes;

  // Wait for the saved text rather than flashing the built-in default first
  if (loading) return <div className="min-h-screen" />;

  if (isRawHtmlPage(liveContent)) {
    return <RawHtmlPage html={liveContent.full_content} />;
  }

  return (
    <div className="bg-[#FAF9F6] min-h-screen text-gray-800 font-sans pb-16">
      {/* Breadcrumb Bar */}
      <div className="bg-white py-3.5 px-4 sm:px-8 text-xs text-gray-500 shadow-xs">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/" className="text-[#03478E] hover:text-[#023366] hover:underline font-medium">
            Home
          </Link>
          <span>&gt;</span>
          <span className="font-semibold text-gray-700">Author Guidelines</span>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <main className="bg-white rounded-3xl p-8 sm:p-12 shadow-sm space-y-10">
          <div>
              <span className="text-xs font-bold text-[#03478E] uppercase tracking-widest block mb-1">
                FOR AUTHORS & SCHOLARS
              </span>
              <h1 className="page-title">
                {titles.page_title || liveContent?.page_title || 'Author Guidelines'}
              </h1>
              {liveContent?.overview && (
                <div className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed border-l-4 border-[#03478E] pl-4 bg-blue-50/40 py-2 rounded-r-xl">
                  <HtmlContent content={liveContent.overview} />
                </div>
              )}
            </div>

            {/* ================= FULL PAGE TEXT (IF ENTERED AS SINGLE DOCUMENT) ================= */}
            {liveContent?.full_content ? (
              <div className="prose max-w-none text-gray-800 leading-relaxed space-y-6 border-t border-gray-100 pt-6">
                <HtmlContent content={liveContent.full_content} />
              </div>
            ) : (
              /* ================= COHESIVE SINGLE PAGE SECTIONS (ALL OPEN) ================= */
              <div className="space-y-10">
                
                {/* 6.1 Manuscript Preparation */}
                <section 
                  id="manuscript-preparation"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-2 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.manuscript_preparation || 'Manuscript Preparation'}
                    </h2>
                  </div>

                  <div className="space-y-6">
                    {/* Overview */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_prep_overview || 'Overview'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.manuscript_preparation}
                        fallback="Manuscripts must present sound clinical or experimental data. Authors are encouraged to prepare their text in standard Microsoft Word (.docx) format with continuous line numbering."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>

                    {/* General Formatting */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_formatting || 'General Formatting'}
                      </h3>
                      <div className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 text-xs sm:text-sm text-gray-700 space-y-2">
                        <div>• <strong>Font:</strong> <HtmlContent inline content={liveContent?.formatting_font} fallback="Times New Roman or Calibri, 12 pt size." /></div>
                        <div>• <strong>Spacing & Margins:</strong> <HtmlContent inline content={liveContent?.formatting_spacing} fallback="1.5 line spacing with 1 inch (2.54 cm) margins on all sides." /></div>
                        <div>• <strong>Remedies:</strong> <HtmlContent inline content={liveContent?.formatting_remedies} fallback="Italicize remedy names on first mention; use official HPI nomenclature." /></div>
                      </div>
                    </div>

                    {/* Structure */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_structure || 'Manuscript Structure (IMRAD)'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.structure}
                        fallback="Original articles must follow standard structure: Title Page (separate file), Blinded Abstract & Keywords (250 words max), Introduction, Materials & Methods, Results, Discussion, Conclusion, and Vancouver-style References."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100"
                      />
                    </div>

                    {/* Conflict of Interest */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_conflict || 'Conflict of Interest Declaration'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.conflict_of_interest}
                        fallback='All authors must declare any commercial, pharmaceutical, financial, or personal relationships that could inappropriately influence their work. If none, state: "The authors declare no conflict of interest."'
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>

                    {/* Funding */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_funding || 'Funding Sources'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.funding}
                        fallback="Disclose all financial support, including government, institutional, or university research grant numbers."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>

                    {/* Originality */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_originality || 'Originality & Duplicate Submission Policy'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.originality}
                        fallback="Manuscripts must be original and not submitted simultaneously elsewhere. Submissions are checked using automated similarity software (max acceptable similarity < 10%)."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>

                    {/* Ethics & Consent */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_ethics || 'Ethical Approval & Patient Consent'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.ethics_and_consent}
                        fallback="Human studies must provide Institutional Ethics Committee (IEC/IRB) approval letter. Clinical case reports require signed informed written patient consent before consideration."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>

                    {/* Tables, Figures & Language */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_figures || 'Tables, Figures & Language Standards'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.tables_figures}
                        fallback="Figures must be min 300 DPI resolution. The official language is clear, grammatical British or American English consistently maintained."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>

                    {/* Author Responsibility */}
                    <div className="space-y-2">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-[#03478E]">
                        {titles.sub_responsibility || 'Author Responsibility'}
                      </h3>
                      <HtmlContent
                        content={liveContent?.author_responsibility}
                        fallback="All listed co-authors must have made substantive scientific contributions to study design, data collection, or critical manuscript revision."
                        className="text-sm sm:text-base text-gray-700 leading-relaxed"
                      />
                    </div>
                  </div>
                </section>

                {/* 6.2 Submission Process */}
                <section 
                  id="submission-process"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.submission_process || 'Submission Process'}
                    </h2>
                  </div>

                  <HtmlContent
                    content={liveContent?.submission_process}
                    fallback="All submissions must be uploaded through the AJHM Online Manuscript Portal."
                    className="text-sm sm:text-base text-gray-700 leading-relaxed"
                  />
                  <div className="pt-2">
                    <Link
                      to="/upload"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#03478E] hover:bg-[#023366] text-white hover:text-white rounded-full text-xs font-bold uppercase tracking-wider shadow-sm hover:shadow-md transition"
                    >
                      <FaUpload className="text-xs" />
                      <span>Launch Submission Portal &rarr;</span>
                    </Link>
                  </div>
                </section>

                {/* 6.3 Article Types */}
                <section 
                  id="article-types"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.article_types || 'Article Types & Word Limits'}
                    </h2>
                  </div>

                  <div className="space-y-3.5">
                    {articleTypes.map((type, idx) => (
                      <div key={idx} className="bg-[#FAFBFD] p-5 rounded-2xl border border-gray-100 space-y-1.5 hover:border-blue-200 transition">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                          <h4 className="font-bold text-sm sm:text-base text-gray-900">{type.title}</h4>
                          <span className="text-xs font-semibold text-[#03478E] bg-blue-50 px-2.5 py-1 rounded-full self-start sm:self-auto">
                            {type.words}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed font-sans">
                          {type.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* 6.4 Article Processing Charge (APC) */}
                <section 
                  id="apc"
                  className="scroll-mt-28 sm:scroll-mt-32 space-y-6 pt-4 border-t border-gray-100"
                >
                  <div className="pb-2 border-b border-gray-100">
                    <h2 className="section-heading">
                      {titles.apc || 'Article Processing Charge (APC)'}
                    </h2>
                  </div>

                  <div className="bg-[#FAFBFD] p-6 rounded-2xl border border-gray-100 space-y-4">
                    <HtmlContent
                      content={liveContent?.apc_text}
                      fallback="Currently, all Article Processing Charges (APCs) for AJHM Volume 1 & 2 are fully waived and sponsored by Domus Publications to support academic homoeopathic scholarship globally. Authors are NOT charged any submission or publishing fees."
                      className="text-sm sm:text-base text-gray-700 leading-relaxed"
                    />
                  </div>
                </section>

              </div>
            )}

            {/* Actions (Single Page Flow) */}
            <div className="border-t border-gray-100 pt-8 flex items-center justify-end gap-3">
              <Link
                to="/upload"
                className="inline-flex items-center gap-2 py-2.5 px-5 bg-[#03478E] hover:bg-[#023366] text-white text-xs font-bold rounded-full uppercase tracking-wider transition-all shadow-xs"
              >
                <FaUpload className="text-[11px]" />
                <span>Submit Manuscript</span>
              </Link>
              <a
                href="/current-issue"
                className="inline-flex items-center gap-2 py-2.5 px-4 bg-white border border-gray-200 text-[#03478E] hover:bg-gray-50 text-xs font-bold rounded-full uppercase tracking-wider transition-all"
              >
                <FaFileAlt className="text-xs" />
                <span>Current Issue</span>
              </a>
            </div>

          </main>
        </div>
      </div>
    );
  };

export default AuthorGuidelinesPage;
